import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import api from '../../config/api';
import { getSessionToken } from '../../utils/authSession';
import FormBanner from './FormBanner';
import './RiderChatModal.css';

const RiderChatModal = ({ isOpen, onClose, orderId, riderInfo }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formBanner, setFormBanner] = useState({ type: '', text: '' });
  const messagesEndRef = useRef(null);

  const getUserId = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const u = JSON.parse(userStr);
        return (u._id || u.id)?.toString();
      }
    } catch (e) {}
    const token = getSessionToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return (payload._id || payload.id || payload.userId || payload.sub)?.toString();
    } catch (e) {
      return null;
    }
  };

  const currentUserId = getUserId();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch past chat messages
  const fetchMessages = async () => {
    if (!orderId) return;
    try {
      let res;
      try {
        res = await api.get(`/chat/${orderId}`);
      } catch (e1) {
        res = await api.get(`/chat/order/${orderId}`);
      }
      const msgList = res.data?.data || res.data?.messages || (Array.isArray(res.data) ? res.data : []);
      if (Array.isArray(msgList)) {
        setMessages(msgList);
      }
    } catch (err) {
      console.warn('Fetch chat messages note:', err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !orderId) return;

    setLoading(true);
    fetchMessages();

    // 4-second polling fallback to guarantee rider messages refresh live
    const interval = setInterval(() => {
      fetchMessages();
    }, 4000);

    return () => clearInterval(interval);
  }, [isOpen, orderId]);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  // Real-time Socket Listener for Chat Messages
  useEffect(() => {
    if (!isOpen || !orderId) return;

    const token = getSessionToken();
    const socketUrl = process.env.REACT_APP_SOCKET_URL ||
      (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api\/?$/, '').replace(/\/+$/, '');

    const socket = io(socketUrl, {
      auth: { token },
      query: { token },
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      socket.emit('join', orderId.toString());
      socket.emit('join', `order:${orderId.toString()}`);
      if (currentUserId) {
        socket.emit('join', `user:${currentUserId}`);
      }
    });

    const handleNewMessage = (newMsg) => {
      if (newMsg) {
        const msgOrderId = (newMsg.order?._id || newMsg.order || newMsg.orderId || '').toString();
        if (!msgOrderId || msgOrderId === orderId.toString()) {
          setMessages(prev => {
            const exists = prev.some(m => (m._id === newMsg._id || m.id === newMsg.id));
            if (exists) return prev;
            return [...prev, newMsg];
          });
        }
      }
    };

    socket.on('chat:new_message', handleNewMessage);
    socket.on('message:new', handleNewMessage);
    socket.on('message', handleNewMessage);

    return () => {
      socket.disconnect();
    };
  }, [isOpen, orderId, currentUserId]);

  // Send Message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || sending) return;

    const receiverId = (riderInfo?.userId || riderInfo?.id || riderInfo?._id || riderInfo?.user?._id || riderInfo?.user)?.toString();
    if (!receiverId) {
      setFormBanner({ type: 'error', text: 'Rider details not assigned yet.' });
      return;
    }

    const textToSend = inputText.trim();
    setInputText('');
    setSending(true);

    try {
      let res;
      try {
        res = await api.post('/chat', {
          orderId,
          receiverId,
          message: textToSend
        });
      } catch (e1) {
        res = await api.post('/chat/send', {
          orderId,
          receiverId,
          message: textToSend
        });
      }

      const newMsg = res.data?.data || {
        _id: Date.now().toString(),
        order: orderId,
        sender: { _id: currentUserId },
        receiver: receiverId,
        message: textToSend,
        createdAt: new Date().toISOString()
      };

      setMessages(prev => {
        const exists = prev.some(m => m._id === newMsg._id);
        if (exists) return prev;
        return [...prev, newMsg];
      });
    } catch (err) {
      setFormBanner({
        type: 'error',
        text: err?.response?.data?.message || 'Failed to send message',
      });
      setInputText(textToSend);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="rider-chat-overlay" onClick={onClose}>
      <div className="rider-chat-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="rider-chat-header">
          <div className="rider-chat-user-info">
            <div className="rider-chat-avatar">
              {riderInfo?.avatar ? (
                <img src={riderInfo.avatar} alt="Rider" />
              ) : (
                <span>🏍️</span>
              )}
            </div>
            <div>
              <div className="rider-chat-name">{riderInfo?.name || 'Delivery Rider'}</div>
              <div className="rider-chat-status">🟢 Online • Order Delivery</div>
            </div>
          </div>
          <button className="rider-chat-close-btn" onClick={onClose}>✕</button>
        </div>

        <FormBanner
          message={formBanner.text}
          type={formBanner.type || 'error'}
          onDismiss={() => setFormBanner({ type: '', text: '' })}
          className="rider-chat-form-banner"
        />

        {/* Messages Body */}
        <div className="rider-chat-body">
          {loading && messages.length === 0 ? (
            <div className="animate-pulse space-y-3 p-4">
              <div style={{ height: 40, width: 200, background: '#f3f4f6', borderRadius: 16 }}></div>
              <div style={{ height: 40, width: 220, background: '#fee2e2', borderRadius: 16, marginLeft: 'auto' }}></div>
              <div style={{ height: 48, width: 260, background: '#f3f4f6', borderRadius: 16 }}></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="rider-chat-empty">
              <span>💬</span>
              <p>No messages yet. Send a message to your rider!</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const senderId = (msg.sender?._id || msg.sender?.id || msg.sender)?.toString();
              const isMe = senderId === currentUserId;
              const msgTime = new Date(msg.createdAt || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

              return (
                <div
                  key={msg._id || index}
                  className={`rider-chat-bubble-wrap ${isMe ? 'me' : 'them'}`}
                >
                  <div className={`rider-chat-bubble ${isMe ? 'me' : 'them'}`}>
                    <div className="rider-chat-text">{msg.message}</div>
                    <div className="rider-chat-time">{msgTime}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Footer */}
        <form className="rider-chat-footer" onSubmit={handleSendMessage}>
          <input
            type="text"
            className="rider-chat-input"
            placeholder="Type a message to rider..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          />
          <button
            type="submit"
            className="rider-chat-send-btn"
            disabled={!inputText.trim() || sending}
          >
            Send ➔
          </button>
        </form>
      </div>
    </div>
  );
};

export default RiderChatModal;
